import { useEffect, useState } from "react";
import { ApiError, type CalendarFeedLink, type CalendarFeedView } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { SettingsCard } from "./SettingsCard";

/**
 * KEY-823: a private link to subscribe to from any calendar app — Apple Calendar, Outlook, Google, mail.com — for
 * calendars Key Dials can't write to. It lists what the person added "just on a dial" to the days of the dials they
 * can see, read-only. Only the link's hash is kept, so the link is shown once, when made; "Reset link" makes a new one
 * and stops the old. Renders nothing when calendars are off (the feed endpoints aren't there).
 */
export function CalendarFeedCard() {
  const [status, setStatus] = useState<CalendarFeedView | null | undefined>(undefined);
  const [link, setLink] = useState<CalendarFeedLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const refresh = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      setStatus(await api.calendarFeed(token));
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  if (!status) return null;

  const make = async () => {
    const token = getAccessToken();
    if (!token || busy) return;
    setBusy(true);
    setNote(null);
    try {
      setLink(await api.resetCalendarFeed(token));
      setConfirmReset(false);
      await refresh();
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "The link couldn't be made just now.");
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    const token = getAccessToken();
    if (!token || busy) return;
    setBusy(true);
    try {
      await api.revokeCalendarFeed(token);
      setLink(null);
      setNote("Feed turned off. Calendars subscribed to it will stop updating.");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setNote("Link copied.");
    } catch {
      setNote("Select the link and copy it.");
    }
  };

  return (
    <SettingsCard
      title="Subscribe from any calendar app"
      hint="For a calendar Key Dials can't add to: a private link your calendar app can subscribe to. It shows what you've added just on a dial, on the days of the dials you can see. It can only be read, and your calendar app decides how often it looks again — often every few hours."
    >
      {link ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            readOnly
            aria-label="Your calendar feed link"
            value={link.url}
            onFocus={(e) => e.currentTarget.select()}
            style={{ font: "inherit", fontSize: 13, padding: "8px 11px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)" }}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="kd-btn kd-btn-sm" onClick={() => void copy()}>Copy link</button>
            <a className="kd-btn kd-btn-sm" href={link.webcalUrl}>Open in my calendar app</a>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Keep it to yourself: anyone with the link sees these entries. It's shown only now — if you lose it, reset it.
          </div>
        </div>
      ) : status.exists ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          You made a feed link on {new Date(status.createdAtUtc!).toLocaleDateString()}. It's only shown when it's made.
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {!status.exists ? (
          <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void make()}>Make my link</button>
        ) : confirmReset ? (
          <>
            <span style={{ fontSize: 13, color: "var(--text)" }}>The old link will stop working.</span>
            <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void make()}>Reset it</button>
            <button type="button" className="kd-btn kd-btn-sm" onClick={() => setConfirmReset(false)}>Keep</button>
          </>
        ) : (
          <>
            <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => setConfirmReset(true)}>Reset link</button>
            <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void turnOff()}>Turn off</button>
          </>
        )}
      </div>
      {note && <div role="status" style={{ fontSize: 13, color: "var(--text-muted)" }}>{note}</div>}
    </SettingsCard>
  );
}
